// Mock data for development and testing

import {
  TranslationsResponse,
  ServerTranslationsResponse,
  TsInitialDataResponse,
  TsAffiliatesResponse,
  TsAffiliatesResponseClicks,
  CheckTsEligibilityResponse,
  GetTsPaymentMethodResponse,
} from "../utils/types";
import { CHECK_TS_ELIGIBILITY_RESPONSE_CODE } from "../utils/enums";

export const mockDataEnabled = false;

/**
 * Eligibility popup manual testing (story 227735):
 * 1. Set `mockDataEnabled = true`
 * 2. Set `mockTsEligibilityReturnCode` to the case you want (1–9), or 0 for success
 * 3. Run login through CVV — the popup should match the selected return code
 *
 * Examples:
 * - 3 → DEBT_FAILURE ("נראה שמשהו השתבש..." + encouragement/support)
 * - 7 → INVALID_PRIVATE_CARD (uses `mockTsPaymentMethodLast4` for card suffix)
 * - 9 → VISA_CARD_FAILURE ("השירות תומך בכרטיסי Mastercard בלבד")
 */
export const mockTsEligibilityReturnCode: number =
  CHECK_TS_ELIGIBILITY_RESPONSE_CODE.VISA_CARD_FAILURE;

/** Last 4 digits used in mock get-ts-payment-method (relevant for return code 7). */
export const mockTsPaymentMethodLast4 = "4321";

const mockEligibilitySuccess = (): CheckTsEligibilityResponse => ({
  feRemainingUsageAmountIl: 25000,
  feUsageAmountIl: 10000,
  returnCode: 0,
  returnMessage: "OK",
  feCustomerFirstName: "דוד",
  feGroupMailingConfirmation: false,
  feCustomerId: "mock-customer-id-encrypted",
});

export const getMockCheckTsEligibilityResponse = (
  returnCode: number = mockTsEligibilityReturnCode,
): CheckTsEligibilityResponse => {
  if (!returnCode) {
    return mockEligibilitySuccess();
  }

  return {
    feRemainingUsageAmountIl: 0,
    feUsageAmountIl: 0,
    returnCode,
    returnMessage: `Mock eligibility failure (returnCode ${returnCode})`,
    ...(returnCode === CHECK_TS_ELIGIBILITY_RESPONSE_CODE.USAGE_LIMIT_FAILURE ||
    returnCode === CHECK_TS_ELIGIBILITY_RESPONSE_CODE.INSUFFICIENT_BALANCE_FAILURE
      ? { feUsageLimitReleaseDate: "2026-07-01T00:00:00.000Z" }
      : {}),
  };
};

export const MOCK_GET_TS_PAYMENT_METHOD_RESPONSE: GetTsPaymentMethodResponse = {
  returnCode: 0,
  returnMessage: "OK",
  feCc4LastDigits: mockTsPaymentMethodLast4,
};
/**
 * Mock translations response from server
 * This simulates the server response format for translations
 * Format: { "he-IL": { key: "value" }, "en-US": { key: "value" } }
 */
export const MOCK_GET_STRINGS_RESPONSE: TranslationsResponse = {
  responseMessage: "Success",
  responseCode: "200",
  responseData: {
    strings: {
      // Common translations
      t_ok: "אישור",
      t_cancel: "ביטול",
      t_close: "סגור",
      t_save: "שמור",
      t_delete: "מחק",
      t_edit: "ערוך",
      t_back: "חזור",
      t_next: "הבא",
      t_previous: "הקודם",
      t_submit: "שלח",
      t_search: "חפש",
      t_filter: "סנן",
      t_reset: "איפוס",
      t_loading: "טוען...",
      t_error: "שגיאה",
      t_success: "הצלחה",
      t_warning: "אזהרה",
      t_info: "מידע",

      // Popup translations
      t_error_popup_title: "שגיאה",
      t_error_popup_body: "אירעה שגיאה. אנא נסה שוב.",
      t_401_error_popup_title: "שגיאת הרשאה",
      t_401_error_popup_body: "אין לך הרשאה לבצע פעולה זו.",
      t_401_error_popup_find_button: "הבנתי",
      t_session_expired_modal_title: "ההתחברות שלך פגה",
      t_session_expired_modal_body:
        "לצורכי אבטחה, ההתחברות שלך הסתיימה. כדי להמשיך, יש להתחבר מחדש.",
      t_session_expired_modal_primary_button: "התחבר מחדש",
      t_error_popup_page_not_found_title: "דף לא נמצא",
      t_success_popup_title: "הצלחה",
      t_success_popup_body: "הפעולה בוצעה בהצלחה.",
      t_confirm_popup_title: "אישור",
      t_confirm_popup_body: "האם אתה בטוח שברצונך לבצע פעולה זו?",

      // Form translations
      t_form_name: "שם",
      t_form_email: "אימייל",
      t_form_phone: "טלפון",
      t_form_address: "כתובת",
      t_form_required: "שדה חובה",
      t_form_invalid_email: "אימייל לא תקין",
      t_form_invalid_phone: "טלפון לא תקין",

      // Navigation
      t_nav_home: "בית",
      t_nav_about: "אודות",
      t_nav_contact: "צור קשר",
      t_nav_profile: "פרופיל",
      t_nav_settings: "הגדרות",

      // Messages
      t_message_saved: "הנתונים נשמרו בהצלחה",
      t_message_deleted: "הפריט נמחק בהצלחה",
      t_message_updated: "הנתונים עודכנו בהצלחה",
      t_message_error: "אירעה שגיאה. אנא נסה שוב.",
      t_message_network_error: "שגיאת רשת. אנא בדוק את החיבור לאינטרנט.",
      t_message_timeout:
        "השרת לא מגיב כרגע. זו בעיה זמנית. אנא נסה שוב בעוד כמה דקות.",
    },
  },
};

/**
 * Mock translations response in server format
 * This is the format expected from the server API
 * Format: { "he-IL": { key: "value" }, "en-US": { key: "value" } }
 * This simulates the raw response from getStrings API endpoint
 */
export const MOCK_SERVER_TRANSLATIONS: ServerTranslationsResponse = {
  "he-IL": {
    // Common translations
    t_ok: "אישור",
    t_cancel: "ביטול",
    t_close: "סגור",
    t_save: "שמור",
    t_delete: "מחק",
    t_edit: "ערוך",
    t_back: "חזור",
    t_next: "הבא",
    t_previous: "הקודם",
    t_submit: "שלח",
    t_search: "חפש",
    t_filter: "סנן",
    t_reset: "איפוס",
    t_loading: "טוען...",
    t_error: "שגיאה",
    t_success: "הצלחה",
    t_warning: "אזהרה",
    t_info: "מידע",

    // Popup translations
    t_error_popup_title: "שגיאה",
    t_error_popup_body: "אירעה שגיאה. אנא נסה שוב.",
    t_401_error_popup_title: "שגיאת הרשאה",
    t_401_error_popup_body: "אין לך הרשאה לבצע פעולה זו.",
    t_401_error_popup_find_button: "הבנתי",
    t_session_expired_modal_title: "ההתחברות שלך פגה",
    t_session_expired_modal_body:
      "לצורכי אבטחה, ההתחברות שלך הסתיימה. כדי להמשיך, יש להתחבר מחדש.",
    t_session_expired_modal_primary_button: "התחבר מחדש",
    t_error_popup_page_not_found_title: "דף לא נמצא",
    t_success_popup_title: "הצלחה",
    t_success_popup_body: "הפעולה בוצעה בהצלחה.",
    t_confirm_popup_title: "אישור",
    t_confirm_popup_body: "האם אתה בטוח שברצונך לבצע פעולה זו?",

    // Form translations
    t_form_name: "שם",
    t_form_email: "אימייל",
    t_form_phone: "טלפון",
    t_form_address: "כתובת",
    t_form_required: "שדה חובה",
    t_form_invalid_email: "אימייל לא תקין",
    t_form_invalid_phone: "טלפון לא תקין",

    // Navigation
    t_nav_home: "בית",
    t_nav_about: "אודות",
    t_nav_contact: "צור קשר",
    t_nav_profile: "פרופיל",
    t_nav_settings: "הגדרות",

    // Messages
    t_message_saved: "הנתונים נשמרו בהצלחה",
    t_message_deleted: "הפריט נמחק בהצלחה",
    t_message_updated: "הנתונים עודכנו בהצלחה",
    t_message_error: "אירעה שגיאה. אנא נסה שוב.",
    t_message_network_error: "שגיאת רשת. אנא בדוק את החיבור לאינטרנט.",
    t_message_timeout: "פג תוקף החיבור. אנא התחבר מחדש.",
  },
  "en-US": {
    // Common translations
    t_ok: "OK",
    t_cancel: "Cancel",
    t_close: "Close",
    t_save: "Save",
    t_delete: "Delete",
    t_edit: "Edit",
    t_back: "Back",
    t_next: "Next",
    t_previous: "Previous",
    t_submit: "Submit",
    t_search: "Search",
    t_filter: "Filter",
    t_reset: "Reset",
    t_loading: "Loading...",
    t_error: "Error",
    t_success: "Success",
    t_warning: "Warning",
    t_info: "Info",

    // Popup translations
    t_error_popup_title: "Error",
    t_error_popup_body: "An error occurred. Please try again.",
    t_401_error_popup_title: "Authorization Error",
    t_401_error_popup_body:
      "You do not have permission to perform this action.",
    t_401_error_popup_find_button: "Find",
    t_session_expired_modal_title: "Your session has expired",
    t_session_expired_modal_body:
      "For security reasons, your session has ended. To continue, please sign in again.",
    t_session_expired_modal_primary_button: "Sign in again",
    t_error_popup_page_not_found_title: "Page Not Found",
    t_success_popup_title: "Success",
    t_success_popup_body: "The operation completed successfully.",
    t_confirm_popup_title: "Confirm",
    t_confirm_popup_body: "Are you sure you want to perform this action?",

    // Form translations
    t_form_name: "Name",
    t_form_email: "Email",
    t_form_phone: "Phone",
    t_form_address: "Address",
    t_form_required: "Required field",
    t_form_invalid_email: "Invalid email",
    t_form_invalid_phone: "Invalid phone",

    // Navigation
    t_nav_home: "Home",
    t_nav_about: "About",
    t_nav_contact: "Contact",
    t_nav_profile: "Profile",
    t_nav_settings: "Settings",

    // Messages
    t_message_saved: "Data saved successfully",
    t_message_deleted: "Item deleted successfully",
    t_message_updated: "Data updated successfully",
    t_message_error: "An error occurred. Please try again.",
    t_message_network_error:
      "Network error. Please check your internet connection.",
    t_message_timeout:
      "The server is not responding at the moment. This is a temporary issue. Please try again in a few minutes.",
  },
};
export const MOCK_GET_TS_INITIAL_DATA_RESPONSE: TsInitialDataResponse = {
  "maxUsageNoRiskAmountIl": 50000,
  "maxUsageRiskAmountIl": 5000,
  "minRemainingUsageAmountIl": 20000,
  "testimonials": [
    {
      "id": "1",
      "text": "חסכתי מעל 1,500 ₪ על הזמנת המלון בפריז! התהליך היה פשוט וקל להפליא. ממליץ בחום!",
      "rating": 5,
      "location": "תל אביב",
      "customerName": "דוד כהן"
    },
    {
      "id": "2",
      "text": "השירות המעולה הזה חסך לי הרבה כסף בהזמנת המלון באיטליה. בנוסף קיבלתי החזר עמלות",
      "rating": 5,
      "location": "ירושלים",
      "customerName": "שרה לוי"
    },
    {
      "id": "3",
      "text": "קל מאוד להשתמש בשירות, והחיסכון אמיתי! הזמנתי מלון בברצלונה וחסכתי כמעט 800 ₪",
      "rating": 5,
      "location": "חיפה",
      "customerName": "מיכאל בן דוד"
    }
  ],
  "faqItems": [
    {
      "id": "1",
      "answer": "פשוט מאוד: נכנסים לאתר, מזדהים כלקוחות פספורטכארד ומפעילים את כרטיס ה-Pay שלכם, בוחרים את אחד השותפים שלנו (כמו Booking.com, Skyscanner או Expedia), ומבצעים את ההזמנה שם כרגיל. בקופה משלמים עם כרטיס ה-Pay – כך ההזמנה מחויבת ללא עמלות המרה ובשער חליפין אמיתי.",
      "question": "מה קורה אם נכנסתי לחנות בלי לעבור דרך האתר?"
    },
    {
      "id": "2",
      "answer": "אנחנו מרכזים עבורכם את כל פלטפורמות הנסיעה המובילות במקום אחד, ודואגים להזכיר לכם להשתמש בפספורטכארד Pay כדי להבטיח חיסכון אמיתי.",
      "question": "למה כדאי לעבור דרככם בדרך לרכישת חופשה?"
    },
    {
      "id": "3",
      "answer": "תוכלו להשתמש בכרטיס רק לאחר שהזדהיתם והפעלתם אותו באתר. לאחר ההזדהות, הכרטיס יפעל רק באמצעות ההפניות שמופיעות מעלה.",
      "question": "האם אפשר להשתמש בכרטיס הפיזי שלי בלי לעבור דרככם?"
    },
    {
      "id": "4",
      "answer": "אנחנו חוסכים לכם ביחסי המרת המט\"ח ובעמלוות ההמרה. את החסכון תוכלו לראות ישירות בחיוב בחשבון הבנק שלכם. השוו עם כלי תשלום אחרים ובדקו אותנו.",
      "question": "איפה אני רואה את החסכון ?"
    },
    {
      "id": "5",
      "answer": "אין בעיה – האתר פתוח לכולם. עם זאת, את החיסכון האמיתי תרגיש רק אם תשלמו בכרטיס פספורטכארד Pay, שכן הוא מבטיח 0% עמלת המרה וללא עמלות עמלות נסתרות.",
      "question": "ואם אני לא לקוח של פספורטכארד?"
    }
  ],
  "tsDiscountPct": 3,
  "usdExchangeRate": 2.895,
  "tsRemainingUsageRoundBasis": 100
}
export const MOCK_GET_TS_AFFILIATES_RESPONSE: TsAffiliatesResponse = {
  "affiliates": [
    {
      "type": "hotels",
      "websites": [
        {
          "id": "2",
          "name": "Booking.com",
          "logo": "https://pfs-assets-dev.passportcard.com/travel-services/affiliates/images/hotels/booking.png",
          "short_desc": "מלונות ואכסניות מהטובים בעולם",
          "link_text": "עברו לתשלום ב-Booking.com"
        },
        {
          "id": "3",
          "name": "Agoda",
          "logo": "https://pfs-assets-dev.passportcard.com/travel-services/affiliates/images/hotels/agoda.png",
          "short_desc": "מלונות במחירים מיוחדים",
          "link_text": "להזמנה באתר לחצו כאן"
        },
        {
          "id": "1",
          "name": "Expedia",
          "logo": "https://pfs-assets-dev.passportcard.com/travel-services/affiliates/images/hotels/expedia.png",
          "short_desc": "מלונות ואכסניות מהטובים בעולם",
          "link_text": "עברו לתשלום ב-Booking.com"
        },
        {
          "id": "4",
          "name": "TripAdvisor",
          "logo": "https://pfs-assets-dev.passportcard.com/travel-services/affiliates/images/hotels/tripadvisor.png",
          "short_desc": "מלונות ואכסניות מהטובים בעולם",
          "link_text": "עברו לתשלום ב-Booking.com"
        },
        {
          "id": "5",
          "name": "Booking.com",
          "logo": "https://pfs-assets-dev.passportcard.com/travel-services/affiliates/images/hotels/booking.png",
          "short_desc": "מלונות ואכסניות מהטובים בעולם",
          "link_text": "עברו לתשלום ב-Booking.com"
        },
        {
          "id": "6",
          "name": "Booking.com",
          "logo": "https://pfs-assets-dev.passportcard.com/travel-services/affiliates/images/hotels/booking.png",
          "short_desc": "מלונות ואכסניות מהטובים בעולם",
          "link_text": "עברו לתשלום ב-Booking.com"
        },
        {
          "id": "7",
          "name": "Booking.com",
          "logo": "https://pfs-assets-dev.passportcard.com/travel-services/affiliates/images/hotels/booking.png",
          "short_desc": "מלונות ואכסניות מהטובים בעולם",
          "link_text": "עברו לתשלום ב-Booking.com"
        }
      ]
    },
    {
      "type": "flights",
      "websites": [
        {
          "id": "6",
          "name": "Skyscanner",
          "logo": "https://pfs-assets-dev.passportcard.com/travel-services/affiliates/flights/Skyscanner.jpg",
          "short_desc": "טיסות במחירים הכי טובים",
          "link_text": "להזמנה באתר לחצו כאן",
          "aggregatorName": "IMPACT"
        },
        {
          "id": "5",
          "name": "Kayak",
          "logo": "https://pfs-assets-dev.passportcard.com/travel-services/affiliates/flights/Kayak.jpg",
          "short_desc": "למצוא טיסות נהיה קל",
          "link_text": "להזמנה באתר לחצו כאן"
        }
      ]
    },
    {
      "type": "attractions",
      "websites": [
        {
          "id": "7",
          "name": "GetYourGuide",
          "logo": "https://pfs-assets-dev.passportcard.com/travel-services/affiliatesGetYourGuide.jpg",
          "short_desc": "מלונות ואכסניות מהטובים בעולם",
          "link_text": "עברו לתשלום ב-Booking.com"
        }
      ]
    }
  ]
}
export const MOCK_GET_TS_AFFILIATES_RESPONSE_CLICKS: TsAffiliatesResponseClicks = {
  "affiliatesTimers": [
    {
      "aggregatorName": "IMPACT",
      "aggregatorTimer": 600000
    },
    {
      "aggregatorName": "TravelPayouts",
      "aggregatorTimer": 50000
    }
  ],
  "affiliates": [
    {
      "type": "hotels",
      "websites": [
        {
          "id": "2",
          "name": "Booking.com",
          "logo": "https://pfs-assets-dev.passportcard.com/travel-services/affiliates/images/hotels/booking.png",
          "short_desc": "מלונות ואכסניות מהטובים בעולם",
          "link_text": "עברו לתשלום ב-Booking.com",
          "affiliateTimer": 600000,
          "feClickId": "mock-extend-ts-click-id",
          "aggregatorName": "TravelPayouts",
          "click_end_date": "2025-12-31T23:59:59.000Z",
          "card_closing_date": "2025-12-31T23:59:59.000Z"
        }
      ]
    },
    {
      "type": "flights",
      "websites": [
        {
          "id": "6",
          "name": "Skyscanner",
          "logo": "https://pfs-assets-dev.passportcard.com/travel-services/affiliates/flights/Skyscanner.jpg",
          "short_desc": "טיסות במחירים הכי טובים",
          "link_text": "להזמנה באתר לחצו כאן",
          "affiliateTimer": 600000,
          "feClickId": "mock-extend-ts-click-id",
          "aggregatorName": "IMPACT",
          "click_end_date": "2025-12-31T23:59:59.000Z",
          "card_closing_date": "2025-12-31T23:59:59.000Z"
        }
      ]
    }
  ]
}